import {
  type OnChangeFn,
  type RowData,
  type Updater,
  functionalUpdate,
} from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import { WrapTextIcon, WrapTextOffIcon } from '@dynatrace/strato-icons';

import { useControllableState } from '../../../core/hooks/useControllableState.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import { dataTableMessages } from '../../DataTableV2/messages.js';
import { useDataTableV2ConfigElement } from '../../hooks/useDataTableV2Config.js';
import { useDataTableV2 } from '../contexts/DataTableV2Context.js';
import {
  type Column,
  type Table,
  type TableFeature,
  type TableOptions,
} from '../hooks/useTable/types.js';

/**
 * @public
 */
export interface DataTableV2LineWrapBaseProps {
  /**
   * Callback that is called when the lineWrap state of the table or any column is changed.
   */
  onLineWrapChange?: (lineWrap: DataTableV2LineWrapState) => void;
}
/**
 * @public
 */
export interface DataTableV2LineWrapControlledProps
  extends DataTableV2LineWrapBaseProps {
  /**
   * Lets you control the lineWrap state of the entire table or individual columns.
   * `boolean` values will affect the whole table, whereas `Record` values will let you assign
   * lineWraps to individual columns.
   * @example
   * ```typescript
   * lineWrap={true} // for forcing lineWrap on all columns in the table
   * lineWrap={{ [column.id]: true|false }} // to control the lineWrap on individual columns.
   * ```
   */
  lineWrap?: DataTableV2LineWrapState;

  /**
   * Lets you set lineWrap state initially of the entire table or individual columns.
   * `boolean` values will affect the whole table, whereas `Record` values will let you assign
   * lineWraps to individual columns.
   * @example
   * ```typescript
   * defaultLineWrap={true} // for forcing lineWrap on all columns in the table
   * defaultLineWrap={{ [column.id]: true|false }} // to control the lineWrap on individual columns.
   * ```
   */
  defaultLineWrap?: never;
}
/**
 * @public
 */
export interface DataTableV2LineWrapUncontrolledProps
  extends DataTableV2LineWrapBaseProps {
  /**
   * Lets you control the lineWrap state of the entire table or individual columns.
   * `boolean` values will affect the whole table, whereas `Record` values will let you assign
   * lineWraps to individual columns.
   * @example
   * ```typescript
   * lineWrap={true} // for forcing lineWrap on all columns in the table
   * lineWrap={{ [column.id]: true|false }} // to control the lineWrap on individual columns.
   * ```
   */
  lineWrap?: never;

  /**
   * Lets you set lineWrap state initially of the entire table or individual columns.
   * `boolean` values will affect the whole table, whereas `Record` values will let you assign
   * lineWraps to individual columns.
   * @example
   * ```typescript
   * defaultLineWrap={true} // for forcing lineWrap on all columns in the table
   * defaultLineWrap={{ [column.id]: true|false }} // to control the lineWrap on individual columns.
   * ```
   */
  defaultLineWrap?: DataTableV2LineWrapState;
}

/**
 * Interface defining the props on the DataTableV2 that control the line wrapping feature.
 * @public
 */
export type DataTableV2LineWrapProps =
  | DataTableV2LineWrapControlledProps
  | DataTableV2LineWrapUncontrolledProps;

/**
 * Global line wrap action for the DataTableV2 to be used in the DataTableV2.Toolbar
 *
 * @public
 */
export function DataTableV2GlobalLineWrapAction() {
  return null;
}

/**
 * Defines the shape of the LineWrap state within the tanstack tables state.
 * @public
 */
export type DataTableV2LineWrapState = Record<string, boolean> | boolean;

/**
 * Extension interface to extend the tanstack table state with the LineWrap state.
 * @internal
 */
export interface DataTableV2LineWrapTableState {
  /**
   * Table state extension for lineWrap
   */
  columnLineWrap: DataTableV2LineWrapState;
}

/**
 * Extension interface to extend the useTable hook options with the LineWrap options.
 * @internal
 */
export interface DataTableV2LineWrapOptions {
  /**
   * Enables line wrapping as a feature on the table.
   */
  enableLineWrap?: boolean;

  /**
   * Exposes the onLineWrapChange function to the Table options.
   */
  onColumnLineWrapChange?: OnChangeFn<DataTableV2LineWrapState>;
}

/**
 * Extension interface to extend the types of the tanstack table instance.
 * @internal
 */
export interface DataTableV2LineWrapInstance {
  /**
   * Helper function to update the global table states `columnLineWrap` portion.
   */
  setColumnLineWrap: (updater: Updater<DataTableV2LineWrapState>) => void;
}

/**
 * Extension interface to extend the types exposed on the tanstack column instance.
 * @internal
 */
export interface DataTableV2LineWrapColumn {
  /**
   * Returns a boolean value that will represent if the column allows line wrapping.
   */
  getCanLineWrap: () => boolean;

  /**
   * Return a boolean value that will represent the current line wrap state of a column.
   */
  getIsLineWrapped: () => boolean;

  /**
   * Toggles the line wrap property of the column
   * @param value - If a value is provided, this function behaves like a setter instead of a toggle.
   */
  toggleLineWrap: (value?: boolean) => void;
}

/**
 * Feature implementation for LineWrap. This contains the logic that will be given
 * to the tanstack table. The implementing interface is important as these are
 * the function that will be called by the tanstack table core during render.
 * @internal
 */
export const LineWrap: TableFeature = {
  createColumn<TData extends RowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    // Add a toggleLineWrap function onto the column so that it can be accessed
    // during rendering.

    column.toggleLineWrap = (value) => {
      if (column.getCanLineWrap()) {
        table.setColumnLineWrap((oldValue) => {
          // Check if the lineWrap was set as a global first
          if (typeof oldValue === 'boolean') {
            const columnIds = table.getAllFlatColumnIdsWithoutBuiltin();

            // when switching from a boolean, we need to preserve the current state of the other columns
            const currentColumnsState: DataTableV2LineWrapState =
              Object.fromEntries(columnIds.map((id) => [id, oldValue]));

            return {
              ...currentColumnsState,
              [column.id]: value ?? !column.getIsLineWrapped(),
            };
          }

          return {
            ...oldValue,
            [column.id]: value ?? !column.getIsLineWrapped(),
          };
        });
      }
    };

    // Helper function that will allow access to the lineWrap state of an
    // individual column.
    column.getIsLineWrapped = () => {
      const lineWrapState = table.getState().columnLineWrap;
      // Boolean values define a global line wrapping on the table.
      if (typeof lineWrapState === 'boolean') {
        return lineWrapState;
      }

      return lineWrapState?.[column.id] ?? false;
    };

    // Helper function that lets a column check during render if can be line-wrapped.
    column.getCanLineWrap = () => {
      return table.options.enableLineWrap ?? true;
    };
  },

  createTable<TData extends RowData>(table: Table<TData>): void {
    table.setColumnLineWrap = (updater) => {
      const newValue = functionalUpdate(
        updater,
        table.getState().columnLineWrap ?? false,
      );
      table.options.onColumnLineWrapChange?.(newValue);
    };
  },
};

/**
 * Configuration hook for the DataTableV2 LineWrap feature.
 * @internal
 */
export function useColumnLineWrap<T>(
  props: DataTableV2LineWrapProps,
  options: TableOptions<T>,
) {
  const configLineWrap = useDataTableV2ConfigElement('lineWrap');

  const [columnLineWrap, onColumnLineWrapChange] = useControllableState({
    value: props.lineWrap ?? configLineWrap,
    defaultValue: props.defaultLineWrap,
    onChange: props.onLineWrapChange,
  });

  options.state = { ...options.state, columnLineWrap };
  options.onColumnLineWrapChange = onColumnLineWrapChange;
}

//# region Providing actions

/**
 * Provides the toolbar button that allows toggling global line wrap.
 * @internal
 */
export function DataTableV2GlobalLineWrapToolbarAction() {
  const table = useDataTableV2();
  const intl = useIntl();

  const lineWrapState = table.getState().columnLineWrap;
  const Icon = lineWrapState === true ? <WrapTextOffIcon /> : <WrapTextIcon />;
  const lineWrapLabel =
    lineWrapState === true
      ? intl.formatMessage(dataTableMessages.disableLineWrapLabel)
      : intl.formatMessage(dataTableMessages.enableLineWrapLabel);

  return (
    <Tooltip text={lineWrapLabel}>
      <Button
        aria-label={lineWrapLabel}
        onClick={() => {
          table.setColumnLineWrap((oldLineWrap) => {
            if (oldLineWrap === true) {
              return {};
            }
            return true;
          });
        }}
      >
        <Button.Prefix>{Icon}</Button.Prefix>
      </Button>
    </Tooltip>
  );
}

//# endregion Providing actions
